import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';

export interface PoliticasPadrao {
  client_id: number;
  pode_selecionar_tabela: boolean;
  tabela_preco_padrao_id: number | null;
  pode_dar_desconto_item: boolean;
  limite_desconto_item: number;
  pode_dar_desconto_total: boolean;
  limite_desconto_total: number;
  origem_pedido_padrao_id: number | null;
  // AIDEV-NOTE: null = todos os pontos de estoque (saldo consolidado)
  ponto_estoque_id: number | null;
  origem_padrao_id: number | null;
  pode_alterar_origem_pedido: boolean;
  empresa_padrao_id: number | null;
  pode_alterar_empresa: boolean;
  pode_cadastrar_clientes: boolean;
  config_sync_pedido: 'automatico' | 'manual';
  tipo_comercializacao_padrao_id: number | null;
  tipo_movimento_receber_padrao_id: number | null;
  pode_alterar_tipo_movimento_receber: boolean;
  exibir_estoque_vendedor: boolean;
  // AIDEV-NOTE: Nível 5 da hierarquia de preços — 'V' = PRECO_VENDA, 'A' = PRECO_ATACADO.
  // Define o preço-base do produto antes de aplicar regras de tabela/desconto.
  tipo_preco_base: 'V' | 'A';
  // AIDEV-NOTE: Campos de alteração de preço unitário — criados na migration 009
  pode_alterar_preco_unitario: boolean;
  limite_reducao_preco_pct: number | null;
  limite_aumento_preco_pct: number | null;
}

export interface RegraTabelaPreco {
  id?: string;
  tabela_preco_id: number;
  quantidade_minima: number;
  quantidade_maxima: number | null;
  ordem: number;
}

export interface RestricaoPagto {
  id?: string;
  condicao_pagto_id: number;
  ativo: boolean;
  valor_minimo_pedido: number | null;
}

// AIDEV-PERF: Queries habilitadas sob demanda via activeTab — evita disparar 3 requests ao montar
export function usePoliticasGlobais(activeTab = 0) {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();

  const politicasQuery = useQuery({
    queryKey: ['admin-politicas-globais'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PoliticasPadrao> => {
      const res = await api.get<{ politicas: PoliticasPadrao }>('/admin/politicas');
      return res.data.politicas;
    }
  });

  const regrasQuery = useQuery({
    queryKey: ['admin-politicas-regras-tabela'],
    enabled: activeTab === 1,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<RegraTabelaPreco[]> => {
      const res = await api.get<{ regras: RegraTabelaPreco[] }>(
        '/admin/politicas/tabelas-preco/regras'
      );
      return res.data.regras;
    }
  });

  const restricoesPagtoQuery = useQuery({
    queryKey: ['admin-politicas-restricoes-pagto'],
    enabled: activeTab === 2,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<RestricaoPagto[]> => {
      const res = await api.get<{ restricoes: RestricaoPagto[] }>(
        '/admin/politicas/condicoes-pagto'
      );
      return res.data.restricoes;
    }
  });

  const savePoliticasMutation = useMutation({
    mutationFn: async (payload: Partial<PoliticasPadrao>) => {
      await api.put('/admin/politicas', payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-politicas-globais'] });
      // AIDEV-NOTE: Atualiza o contexto de auth para refletir políticas novas imediatamente,
      // sem exigir reload da página (user.exibir_estoque_vendedor e outros campos de política).
      await refreshUser();
    }
  });

  const saveRegrasMutation = useMutation({
    mutationFn: async (regras: Omit<RegraTabelaPreco, 'id'>[]) => {
      await api.put('/admin/politicas/tabelas-preco/regras', { regras });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-politicas-regras-tabela'] })
  });

  const saveRestricoesPagtoMutation = useMutation({
    mutationFn: async (restricoes: Omit<RestricaoPagto, 'id'>[]) => {
      await api.put('/admin/politicas/condicoes-pagto', { restricoes });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-politicas-restricoes-pagto'] })
  });

  return {
    politicasQuery,
    regrasQuery,
    restricoesPagtoQuery,
    savePoliticasMutation,
    saveRegrasMutation,
    saveRestricoesPagtoMutation
  };
}
